"use client";

import { GenuiSectionIcon } from "./GenuiSectionIcon";
import type { InsightsGenuiTable as GenuiTableSpec } from "./parse-genui";
import { cn } from "@/shared/lib/utils/cn";

/** Wrap long prose cells; short/numeric columns stay nowrap and size to content. */
const WRAP_CHAR_THRESHOLD = 48;

function columnShouldWrap(header: string, rows: GenuiTableSpec["rows"], colIndex: number): boolean {
  let longest = header.trim().length;
  for (const row of rows) {
    const cell = row[colIndex];
    if (cell == null || cell === "") continue;
    const len = String(cell).trim().length;
    if (len > longest) longest = len;
    if (longest >= WRAP_CHAR_THRESHOLD) return true;
  }
  return longest >= WRAP_CHAR_THRESHOLD;
}

function cellClass(wrap: boolean): string {
  if (wrap) {
    return "max-w-[18rem] whitespace-normal break-words border-b border-r border-border/40 px-3.5 py-2 align-top font-normal text-foreground last:border-r-0";
  }
  return "whitespace-nowrap border-b border-r border-border/40 px-3.5 py-2 align-top font-normal text-foreground last:border-r-0";
}

/**
 * Native table for GenUI `{ type: "table", ... }` payloads inside <render>.
 * Caller: InsightsAnswer. Columns size to content (auto layout + nowrap); long
 * prose columns wrap with a max width based on cell length, not header names.
 */
export function InsightsGenuiTable({
  spec,
  icon,
  live = false,
}: {
  spec: GenuiTableSpec;
  icon?: string;
  live?: boolean;
}) {
  const columns = spec.columns.length > 0 ? spec.columns : live ? ["…", "…", "…"] : [];

  const wrapFlags = columns.map((col, index) => columnShouldWrap(col, spec.rows, index));

  return (
    <figure className={cn("my-0 w-full min-w-0 bg-background", live && "bg-primary/[0.02]")}>
      {spec.title ? (
        <figcaption className="flex items-center gap-2.5 border-b border-border/50 bg-muted/30 px-3.5 py-2.5 text-[16px] font-semibold leading-snug text-foreground">
          <GenuiSectionIcon icon={icon} hint={spec.title} kind="table" />
          <span className="min-w-0">{spec.title}</span>
        </figcaption>
      ) : live ? (
        <figcaption className="border-b border-border/50 bg-muted/30 px-3.5 py-2.5">
          <span className="inline-block h-3.5 w-40 animate-pulse rounded bg-muted" />
        </figcaption>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-max min-w-full table-auto border-collapse font-sans text-left text-[14px] font-normal leading-[1.55]">
          <thead>
            <tr className="bg-muted/40">
              {columns.map((col, index) => (
                <th
                  key={`${col}-${index}`}
                  className={cn(
                    "border-b border-r border-border px-3.5 py-2 font-semibold text-foreground last:border-r-0",
                    wrapFlags[index] ? "max-w-[18rem] whitespace-normal" : "whitespace-nowrap",
                  )}
                >
                  {spec.columns.length === 0 && live ? (
                    <span className="inline-block h-3 w-16 animate-pulse rounded bg-muted" />
                  ) : (
                    col
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spec.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="odd:bg-background even:bg-muted/20">
                {columns.map((_col, colIndex) => (
                  <td key={colIndex} className={cellClass(Boolean(wrapFlags[colIndex]))}>
                    {row[colIndex] == null || row[colIndex] === "" ? "—" : String(row[colIndex])}
                  </td>
                ))}
              </tr>
            ))}
            {live ? (
              <tr className="odd:bg-background even:bg-muted/20">
                {columns.map((_col, colIndex) => (
                  <td
                    key={`pending-${colIndex}`}
                    className={cellClass(Boolean(wrapFlags[colIndex]))}
                  >
                    <span className="inline-block h-3 w-14 animate-pulse rounded bg-muted/80" />
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
